import { useEffect, useRef } from 'react';
import type { BadgeView } from '@contracts';
import { BadgeGraphic } from '@ui/shared/badges/BadgeGraphic';
import { describeCriterion } from '@ui/shared/badges/criterionText';
import { X } from '@ui/shared/icons';

/**
 * Badge detail sheet (settings-rewards.md §3: "Click -> detail sheet...
 * large badge (160px), name, the same criterion sentence at larger size,
 * earned timestamp if earned, and Edit / New badge actions. The sheet
 * restates rather than reveals — everything in it was already readable in
 * the grid."). `onEdit` is only offered for non-starter badges — starter
 * badges are structurally fixed (`STARTER_BADGES` in reward-config.ts);
 * editing them isn't part of this round's scope (the spec's badge editor is
 * for USER-CREATED badges, R7.10) and a starter badge's `isStarter` flag is
 * exactly the signal for that.
 */
export function BadgeDetailSheet({
  badge,
  onClose,
  onEdit,
  onArchive,
}: {
  readonly badge: BadgeView;
  readonly onClose: () => void;
  readonly onEdit?: () => void;
  readonly onArchive?: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    panelRef.current?.focus();
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const progressFraction = badge.state.earned
    ? 1
    : badge.state.progress.target > 0
      ? Math.min(1, badge.state.progress.current / badge.state.progress.target)
      : 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${badge.name} details`}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'color-mix(in srgb, var(--surface-base) 70%, transparent)',
        padding: 'var(--space-6)',
      }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'var(--space-4)',
          background: 'var(--surface-overlay)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-2)',
          padding: 'var(--space-8)',
          maxWidth: '360px',
          textAlign: 'center',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: 'var(--space-3)',
            right: 'var(--space-3)',
            width: 'var(--touch-target-min)',
            height: 'var(--touch-target-min)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-muted)',
          }}
        >
          <X size={18} aria-hidden="true" />
        </button>

        <BadgeGraphic iconId={badge.iconId} earned={badge.state.earned} category={null} progressFraction={progressFraction} size={160} />

        <h2 style={{ margin: 0, fontSize: 'var(--type-heading-size)', fontWeight: 'var(--type-heading-weight)', color: 'var(--text-primary)' }}>
          {badge.name}
        </h2>

        <p style={{ margin: 0, fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
          {describeCriterion(badge.criterion)}
        </p>

        <p className="num" style={{ margin: 0, fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
          {badge.state.earned
            ? `Earned ${badge.state.earnedOn}.`
            : `${badge.state.progress.current} of ${badge.state.progress.target} done.`}
        </p>

        {(onEdit || onArchive) && !badge.isStarter && (
          <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-2)' }}>
            {onEdit && (
              <button
                type="button"
                onClick={onEdit}
                style={{
                  minHeight: 'var(--touch-target-min)',
                  padding: '0 var(--space-4)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-strong)',
                  color: 'var(--text-secondary)',
                  fontWeight: 500,
                  fontSize: 'var(--type-caption-size)',
                }}
              >
                Edit
              </button>
            )}
            {onArchive && (
              <button
                type="button"
                onClick={onArchive}
                style={{
                  minHeight: 'var(--touch-target-min)',
                  padding: '0 var(--space-4)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-strong)',
                  color: 'var(--text-secondary)',
                  fontWeight: 500,
                  fontSize: 'var(--type-caption-size)',
                }}
              >
                Archive badge
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
